import type { Editor as TiptapEditor } from "@tiptap/react"
import Client from "../../models/client"
import React, { useState } from "react"
import { notifications } from "@mantine/notifications"
import { postItem } from "../api"
import { ImagePlusIcon } from "../tiptap-icons/image-plus-icon"
import { BaseModal } from "./BaseModal"
import { Box, Button, Group, ScrollArea, Stack, Tabs, Text, TextInput } from "@mantine/core"
import { ImageDropzone } from "./dropzoneImageField"

interface Props {
  editor: TiptapEditor
  client: Client
}

const dropzoneStyle = {
  width: '100%',
  minHeight: 90,
  border: '2px dashed #ccc',
  borderRadius: 8,
  padding: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  textAlign: 'center' as const,
  backgroundColor: '#fff'
}

const ImageModal: React.FC<Props> = ({ editor, client }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [directory, setDirectory] = useState(client.url_name ?? '')
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload')

  const closeModal = () => {
    setUrl('')
    setFile(null)
    setDirectory(client.url_name ?? '')
    setIsOpen(false)
    setActiveTab('upload')
  }

  const handleAddByUrl = () => {
    if (url.trim() === '') {
      notifications.show({ message: '画像のURLを入力してください', color: 'red' })
      return
    }
    editor.chain().focus().setImage({ src: url }).run()
    closeModal()
  }

  const handleUpload = async () => {
    setUploading(true)
    try {
      const res = await postItem(client.id, file, directory)
      editor.chain().focus().setImage({ src: res.data.file_path }).run()
      notifications.show({ message: '画像をアップロードしました', color: 'teal' })
      closeModal()
    } catch (e: any) {
      console.error('画像のアップロードに失敗しました', e.response?.status, e.response?.data)
      const d = e.response?.data
      notifications.show({
        message: d?.errors?.join('\n') ?? d?.error ?? 'アップロードに失敗しました',
        color: 'red',
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <button onClick={() => setIsOpen(true)} type="button">
        <ImagePlusIcon width={16} />
      </button>

      <BaseModal
        opened={isOpen}
        onClose={closeModal}
        title="画像挿入"
        width={600}
        zIndex={1000}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <Tabs
              value={activeTab}
              onChange={(v) => setActiveTab((v as 'upload' | 'url') ?? 'upload')}
            >
              <Tabs.List>
                <Tabs.Tab value="upload">画像アップロード</Tabs.Tab>
                <Tabs.Tab value="url">画像URL</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="upload" pt="md">
                <Stack gap="sm">
                  <ImageDropzone
                    onDrop={(accepted: File[]) => setFile(accepted?.[0] ?? null)}
                    style={dropzoneStyle}
                  />
                  {file && (
                    <Text size="sm" c="#000000CC">
                      選択中：{file.name}
                    </Text>
                  )}
                  <TextInput
                    label="アップロード先ディレクトリ"
                    description={`/upload/${directory}`}
                    value={directory}
                    onChange={(e) => setDirectory(e.currentTarget.value)}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="url" pt="md">
                <Stack gap="sm">
                  <TextInput
                    label="画像のURL"
                    placeholder="https://..."
                    value={url}
                    onChange={(e) => setUrl(e.currentTarget.value)}
                  />
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={closeModal}
              disabled={uploading}
            >
              キャンセル
            </Button>
            {activeTab === 'upload' ? (
              <Button
                variant="filled"
                radius="xl"
                onClick={handleUpload}
                loading={uploading}
                disabled={!file}
              >
                アップロード
              </Button>
            ) : (
              <Button
                variant="filled"
                radius="xl"
                onClick={handleAddByUrl}
              >
                画像追加
              </Button>
            )}
          </Group>
        </Box>
      </BaseModal>
    </>
  )
}

export default ImageModal
